import React, { useState, useEffect, useCallback } from 'react';
import Icon from './Icons';

let toastId = 0;
let toastListeners = [];

export const toast = {
  show: (options) => {
    const id = ++toastId;
    const t = { id, ...options };
    toastListeners.forEach((fn) => fn(t));
    return id;
  },
  success: (message, opts = {}) => toast.show({ ...opts, type: 'success', message }),
  error: (message, opts = {}) => toast.show({ ...opts, type: 'error', message }),
  info: (message, opts = {}) => toast.show({ ...opts, type: 'info', message }),
  warning: (message, opts = {}) => toast.show({ ...opts, type: 'warning', message }),
};

const EXIT_MS = 300;

const typeConfig = {
  success: { icon: 'checkCircle', color: 'text-success', bg: 'bg-success-muted', border: 'border-success/20' },
  error: { icon: 'xCircle2', color: 'text-error', bg: 'bg-error-muted', border: 'border-error/20' },
  info: { icon: 'info', color: 'text-info', bg: 'bg-info-muted', border: 'border-info/20' },
  warning: { icon: 'alertCircle', color: 'text-warning', bg: 'bg-warning-muted', border: 'border-warning/20' },
};

const ToastItem = ({ toast: t, onDismiss }) => {
  const config = typeConfig[t.type] || typeConfig.info;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(() => onDismiss(t.id), EXIT_MS);
    }, t.duration || 4000);
    return () => clearTimeout(timer);
  }, [t.id, t.duration, onDismiss]);

  return (
    <div
      className={`
        flex items-start gap-3 w-full max-w-sm p-4
        bg-surface-1 border ${config.border} rounded-xl shadow-lg
        transition-all duration-300 ease-out
        ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}
      `}
      role="alert"
      aria-live="polite"
    >
      <span className={`mt-0.5 ${config.color}`}>
        <Icon name={config.icon} size={18} />
      </span>
      <p className="flex-1 text-sm text-text-primary leading-snug">
        {t.message}
      </p>
      <button
        onClick={() => {
          setVisible(false);
          setTimeout(() => onDismiss(t.id), EXIT_MS);
        }}
        className="p-0.5 text-text-muted hover:text-text-primary transition-colors"
        aria-label="Закрыть уведомление"
      >
        <Icon name="x" size={14} />
      </button>
    </div>
  );
};

export const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((t) => {
    setToasts((prev) => [...prev, t]);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    toastListeners.push(addToast);
    return () => {
      toastListeners = toastListeners.filter((fn) => fn !== addToast);
    };
  }, [addToast]);

  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-toast flex flex-col gap-2 items-end"
      aria-label="Notifications"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={dismissToast} />
      ))}
    </div>
  );
};

export default ToastContainer;
